import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { House, UsersRound, Trophy, CalendarDays } from 'lucide-react';
import { tenantSlugFromHost } from '../../../utils/tenantDomains';
import './PlayerLiquidNavigation.css';

import { tap } from './playerHelpers';

const TABS = [
  { id: 'home', label: 'Home', suffix: '', Icon: House },
  { id: 'referrals', label: 'Referrals', suffix: '/explore/referral', Icon: UsersRound },
  { id: 'achievements', label: 'Achievements', suffix: '/achievements', Icon: Trophy },
  { id: 'events', label: 'Events', suffix: '/events', Icon: CalendarDays },
];
export const playerNavigationBase = (tenantSlug, hostTenant = tenantSlugFromHost()) =>
  !hostTenant && tenantSlug ? `/${tenantSlug}/app` : '/app';

// Also catch portalled game/announcement dialogs whose state belongs to a child.
const OPEN_DIALOG = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], dialog[open], [aria-modal="true"]:not([data-state="closed"])';

export default function PlayerLiquidNavigation({ active = 'home', hidden = false }) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const nav = useRef(null);
  // Some touch browsers dispatch click after a drag. Reject only that press;
  // a fresh tap resets the guard, and keyboard activation has detail zero.
  const press = useRef(null);
  const trackPress = event => {
    const p = press.current;
    if (p && p.id === event.pointerId && Math.hypot(event.clientX - p.x, event.clientY - p.y) > 9) p.dragged = true;
  };
  const [compact, setCompact] = useState(false);
  const [keyboard, setKeyboard] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const blocked = hidden || dialogOpen;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const selected = Math.max(0, TABS.findIndex(tab => tab.id === active));

  useEffect(() => {
    let wasOpen = false;
    const check = () => {
      const isOpen = Boolean(document.querySelector(OPEN_DIALOG));
      if (wasOpen !== isOpen) { wasOpen = isOpen; setDialogOpen(isOpen); }
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'data-state', 'aria-modal', 'role'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setCompact(false);
    let last = 0, direction = 0, distance = 0, frame = 0;
    const read = () => {
      frame = 0;
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.max(0, Math.min(max, window.scrollY));
      const delta = y - last;
      last = y;
      if (blockedRef.current) { distance = 0; return; }
      if (y < 24) { distance = 0; setCompact(false); return; }
      if (Math.abs(delta) < 1) return;
      const nextDirection = Math.sign(delta);
      if (direction !== nextDirection) { direction = nextDirection; distance = 0; }
      distance += Math.abs(delta);
      if (distance >= (direction > 0 ? 36 : 18)) {
        if (!nav.current?.querySelector(':focus-visible')) setCompact(direction > 0);
        distance = 0;
      }
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(read); };
    const onKey = () => setKeyboard(true);
    const onPointer = () => setKeyboard(false);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      cancelAnimationFrame(frame);
    };
  }, [active]);

  return <nav ref={nav} hidden={blocked} aria-label="Player navigation" data-testid="player-liquid-navigation"
    data-state={compact ? 'compact' : 'expanded'}
    className={`player-liquid-nav${compact ? ' is-compact' : ''}${keyboard ? ' is-keyboard' : ''}`}
    onPointerDown={e => { press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dragged: !e.isPrimary, button: e.target.closest('button') }; }}
    onPointerMove={trackPress} onPointerUp={trackPress}
    onPointerCancel={() => { if (press.current) press.current.dragged = true; }}
    onClickCapture={e => {
      if (e.detail !== 0 && press.current?.dragged) { e.preventDefault(); e.stopPropagation(); }
      press.current = null;
    }}
    onFocus={() => { if (keyboard) setCompact(false); }}>
    <div className="player-liquid-nav__glass" aria-hidden="true" />
    <div className="player-liquid-nav__track">
      <span className="player-liquid-nav__selection" style={{ transform: `translateX(${selected * 100}%)` }} aria-hidden="true"><span /></span>
      {TABS.map(({ id, label, suffix, Icon }) => <button key={id} type="button" aria-label={label}
        aria-current={active === id ? 'page' : undefined} className="player-liquid-nav__item"
        onTouchEnd={e => {
          const start = press.current, end = e.changedTouches[0];
          if (!start || !end) return; // Native click fallback without Pointer Events.
          // Touch browsers can omit click just after a scroll. Handle one tap
          // here; cancel its compatibility click rather than navigating twice.
          if (!e.cancelable) return;
          e.preventDefault();
          const hit = document.elementFromPoint(end.clientX, end.clientY);
          if (e.touches.length || start.dragged || start.button !== e.currentTarget ||
              !e.currentTarget.contains(hit) || Math.hypot(end.clientX - start.x, end.clientY - start.y) > 9) return;
          if (!blockedRef.current && active !== id) tap(10);
          e.currentTarget.click(); // Use the same activation path as keyboard and AT.
        }}
        onClick={e => {
          if (blockedRef.current) return;
          if (active === id) { window.scrollTo({ top: 0, behavior: 'instant' }); setCompact(false); }
          else { if (e.detail !== 0) tap(10); navigate(`${playerNavigationBase(tenantSlug)}${suffix}`); }
        }}>
        <Icon size={20} strokeWidth={active === id ? 2.5 : 1.9} aria-hidden="true" />
        <span>{label}</span>
      </button>)}
    </div>
  </nav>;
}
